import React from "react";
import { useSelector } from "react-redux";
import DateFilterWrap from "components/DateFilter/wrap";
import get from "lodash/get";
import useTranslation from "hooks/useTranslation";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "services/api";
import Spinner from "components/Spinner";
import TableRestaurantInventoryMetrics from "pages/Main/Storage/components/TableRestaurantInventoryMetrics";
import { parse, stringify } from "qs";
import classNames from "classnames";
import { AddButton, Modal } from "components";
import ConsumptionModal from "pages/Storage/Consumption/consumptionModal";
import ConsumptionHFPModal from "pages/Storage/Consumption/consumptionHFPModal";
import useAutoReducer from "useAutoReducer";
import { useDateQueryParams } from "hooks/useDateQueryParams";

const InventoryMetrics = ({ consumption, adjustment, index, currency }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const query = parse(location.search, { ignoreQueryPrefix: true });
	const { tr } = useTranslation();

	return (
		<>
			<div className="bg-gray-100 overflow-hidden !rounded-md !border-4 !border-gray-900">
				<div className="bg-gray-200 p-3 border-b border-gray-300 border-l-4 border-l-blue-500 flex items-center justify-between">
					<h3 className="mb-0 text-xl font-medium">
						{index + 1}. {get(consumption, "name")}
					</h3>
				</div>

				<div className="py-5 px-7">
					<TableRestaurantInventoryMetrics
						data={consumption}
						title={tr("Расход")}
						currency={currency}
						type="consumption"
						onStorageClick={storage => {
							navigate({
								pathname: `/storage/consumption/${storage.id}`,
								search: stringify({
									type: "consumption",
									start_at: query.start_at,
									end_at: query.end_at
								})
							});
						}}
					/>
					<TableRestaurantInventoryMetrics
						data={adjustment}
						title={tr("Инвентаризация")}
						currency={currency}
						type="inventory"
						onStorageClick={storage => {
							navigate({
								pathname: `/storage/consumption/${storage.id}`,
								search: stringify({
									type: "adjustment",
									start_at: query.start_at,
									end_at: query.end_at
								})
							});
						}}
					/>
				</div>
			</div>
		</>
	);
};

function MainOrganizationStorageConsumption({ isMainPage }) {
	const { tr } = useTranslation();
	const default_currency_code = useSelector(
		state => state.auth.default_currency_code
	);
	const currency = default_currency_code && default_currency_code.toUpperCase();

	const [state, send] = useAutoReducer({
		isConsumptionModalOpen: false,
		isConsumptionHFPModalOpen: false
	});

	const { start_at, end_at } = useDateQueryParams();

	const queryStorageConsumptions = useQuery({
		queryKey: ["queryStorageConsumptions", start_at, end_at],
		queryFn: () =>
			api.request({
				url: "/main/reports/consumptions",
				params: {
					sort: "id",
					limit: 100,
					start_at: start_at,
					end_at: end_at
				}
			})
	});

	const data = get(queryStorageConsumptions, "data.data", {});

	if (queryStorageConsumptions.status === "loading") {
		return (
			<div className="flex justify-center items-center h-[300px]">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<Modal
				size={1200}
				isOpen={state.isConsumptionModalOpen}
				toggle={() => send({ isConsumptionModalOpen: false })}
			>
				<ConsumptionModal
					showConsumptionModal={() => send({ isConsumptionModalOpen: true })}
					setCanUpdate={() => queryStorageConsumptions.refetch()}
				/>
			</Modal>

			<Modal
				size={1200}
				isOpen={state.isConsumptionHFPModalOpen}
				toggle={() => send({ isConsumptionHFPModalOpen: false })}
			>
				<ConsumptionHFPModal
					showConsumptionModalHFP={() => {
						send({ isConsumptionHFPModalOpen: false });
					}}
					setCanUpdate={() => queryStorageConsumptions.refetch()}
				/>
			</Modal>

			<div className="flex items-center justify-between mb-20">
				<div className="flex items-center gap-5">
					<DateFilterWrap
						initialDefault={"day"}
						withStartHoursAppended={true}
						navigateByDay={true}
					/>
					<h3 className="my-0 font-bold text-xl">{tr("Расход товаров")}</h3>
				</div>

				<div className="flex items-center gap-3">
					<AddButton
						title={tr("Добавить расход")}
						handleClick={() => {
							send({ isConsumptionModalOpen: true });
						}}
					/>
					<AddButton
						title={tr("Добавить категорию полуфабриката")}
						handleClick={() => {
							send({ isConsumptionHFPModalOpen: true });
						}}
					/>
				</div>
			</div>

			<div
				className={classNames({
					"d-panel": !isMainPage
				})}
			>
				<div className="grid grid-cols-1 gap-10">
					{get(data, "consumptions", []).map((consumption, idx) => (
						<InventoryMetrics
							key={idx}
							consumption={consumption}
							adjustment={get(data, `adjustments.${idx}`, [])}
							index={idx}
							currency={currency}
						/>
					))}
				</div>
			</div>
		</>
	);
}

export default MainOrganizationStorageConsumption;
